import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import {
  allProgramDays,
  type ProgramDayMeta,
} from "@/features/programs/data/program-days";

type Props = {
  currentDay: number;
  durationDays: number;
  selectedDay: number;
  onSelectDay: (day: number) => void;
};

/**
 * 21-day soft progress grid. Past days = done, current = active, future = upcoming.
 * No red "missed", user can open any past/current day calmly.
 */
export function ProgramDayGrid({
  currentDay,
  durationDays,
  selectedDay,
  onSelectDay,
}: Props) {
  const { t } = useTranslation();
  const days = allProgramDays(durationDays);

  return (
    <div
      className="grid grid-cols-7 gap-2"
      role="list"
      aria-label={t("programs.daysAria")}
    >
      {days.map((d) => {
        const state = dayState(d.day, currentDay);
        const selected = d.day === selectedDay;
        return (
          <button
            key={d.day}
            type="button"
            role="listitem"
            aria-current={selected ? "true" : undefined}
            aria-label={t("programs.dayAria", {
              day: d.day,
              theme: d.theme,
            })}
            onClick={() => onSelectDay(d.day)}
            className={cn(
              "flex min-h-11 flex-col items-center justify-center rounded-xl border text-xs font-medium transition-colors",
              state === "done" &&
                "border-secondary/30 bg-secondary-soft text-secondary",
              state === "current" &&
                "border-primary bg-primary-soft text-primary shadow-sm",
              state === "upcoming" &&
                "border-border bg-surface-muted text-foreground-subtle",
              selected && "ring-2 ring-ring ring-offset-2 ring-offset-background"
            )}
          >
            <span className="tabular-nums">{d.day}</span>
          </button>
        );
      })}
    </div>
  );
}

export function ProgramDayDetail({ day }: { day: ProgramDayMeta }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium uppercase tracking-wide text-foreground-subtle">
        {t("programs.dayOf", { day: day.day, total: 21 })} · {day.theme}
      </p>
      <p className="text-sm text-foreground-muted leading-relaxed">{day.blurb}</p>
    </div>
  );
}

function dayState(
  day: number,
  currentDay: number
): "done" | "current" | "upcoming" {
  if (day < currentDay) return "done";
  if (day === currentDay) return "current";
  return "upcoming";
}
